import { Tag, UITag, WordIndices } from '../types/tag'
import { Word } from '../models/word'

export const joinTagsIndices = (indices: WordIndices[]): WordIndices[] => {
  const result: WordIndices[] = []
  indices.forEach((idx, index) => {
    const idxs = [...indices]
    idxs.splice(index, 1)
    const toJoin = idxs.find(
      anotherIdx =>
        (anotherIdx.startWordIndex >= idx.startWordIndex &&
          anotherIdx.endWordIndex <= idx.endWordIndex) ||
        (anotherIdx.endWordIndex >= idx.startWordIndex &&
          anotherIdx.endWordIndex <= idx.endWordIndex) ||
        (anotherIdx.startWordIndex <= idx.endWordIndex &&
          anotherIdx.endWordIndex >= idx.endWordIndex)
    )

    if (toJoin) {
      result.push({
        startWordIndex: Math.min(
          ...[idx.startWordIndex, toJoin.startWordIndex]
        ),
        endWordIndex: Math.max(...[idx.endWordIndex, toJoin.endWordIndex])
      })
    } else {
      result.push(idx)
    }
  })

  return result
    .map((idx, index, arr) => {
      const prev = arr[index - 1]
      if (
        prev &&
        prev.startWordIndex === idx.startWordIndex &&
        prev.endWordIndex === idx.endWordIndex
      ) {
        return null
      }
      return idx
    })
    .filter(e => e !== null) as WordIndices[]
}

export const groupSimilarTags = (tags: UITag[]): UITag[] => {
  const result: UITag[] = []
  tags.forEach((tag: UITag) => {
    if (
      !result.find(
        t =>
          t.type === tag.type &&
          t.category === tag.category &&
          t.subCategory === tag.subCategory
      )
    ) {
      const similarTags = tags.filter(
        t =>
          t.type === tag.type &&
          t.category === tag.category &&
          t.subCategory === tag.subCategory
      )

      if (similarTags.length > 1) {
        const idx = similarTags.findIndex(tag => tag.wordIndices)
        let idxs: WordIndices[] = []
        if (idx !== -1) {
          idxs = similarTags[idx].wordIndices || []
        }

        result.push({
          ...similarTags[0],
          wordIndices: joinTagsIndices([
            ...idxs,
            ...similarTags
              .filter(t => !t.wordIndices)
              .map(t => ({
                startWordIndex: t.startWordIndex,
                endWordIndex: t.endWordIndex
              }))
          ])
        })
      } else {
        result.push({ ...similarTags[0] })
      }
    }
  })

  return result
}

export const groupSimilarAnalysisTags = (tags: UITag[]): UITag[] => {
  const result: UITag[] = []
  tags.forEach((tag: UITag) => {
    if (!result.find(t => t.subCategory === tag.subCategory)) {
      const similarTags = tags.filter(t => t.subCategory === tag.subCategory)

      if (similarTags.length > 1) {
        const idx = similarTags.findIndex(tag => tag.wordIndices)
        let idxs: WordIndices[] = []
        if (idx !== -1) {
          idxs = similarTags[idx].wordIndices || []
        }

        result.push({
          ...similarTags[0],
          wordIndices: joinTagsIndices([
            ...idxs,
            ...similarTags
              .filter(t => !t.wordIndices)
              .map(t => ({
                startWordIndex: t.startWordIndex,
                endWordIndex: t.endWordIndex
              }))
          ])
        })
      } else {
        result.push({ ...similarTags[0] })
      }
    }
  })

  return result
}

export function ungroupSimilarTags(tags: Tag[]): Tag[] {
  const result: Tag[] = []

  tags.forEach((tag: Tag) => {
    if (!tag.wordIndices) {
      result.push({ ...tag })
    } else {
      tag.wordIndices.forEach(i => {
        result.push({
          ...tag,
          startWordIndex: i.startWordIndex,
          endWordIndex: i.endWordIndex,
          wordIndices: undefined
        })
      })
    }
  })

  return result
}

export function getWordIndex(word: HTMLElement | Word, words: Word[]): number {
  if (word instanceof HTMLElement) {
    if (!(word && word.dataset && word.dataset.timecodeStart)) {
      return -1
    }
    return words.findIndex(
      (w: Word) => w.start === parseInt(word.dataset.timecodeStart as string)
    )
  }

  return words.findIndex((w: Word) => w.start === word.start)
}

export function getTagStartIndex(tag: Tag, wordIndex: number): number {
  if (!tag.wordIndices) {
    return tag.startWordIndex
  }

  const t = tag.wordIndices.filter(
    i => i.startWordIndex <= wordIndex && wordIndex <= i.endWordIndex
  )
  if (t.length) {
    return t[0].startWordIndex
  }

  return -1
}

export function getTagEndIndex(tag: Tag, wordIndex: number): number {
  if (!tag.wordIndices) {
    return tag.endWordIndex
  }

  const t = tag.wordIndices.filter(
    i => i.startWordIndex <= wordIndex && wordIndex <= i.endWordIndex
  )
  if (t.length) {
    return t[0].endWordIndex
  }

  return -1
}
